import {
  useGetOrdersQuery,
  useGetAllOrdersQuery,
} from "@features/orders/orders-slice"

interface UseGetTestOrLiveOrdersProps {
  tenantid: string
  startDate?: string
  endDate?: string
  uid?: string
  start?: number
  qty?: number
  role?: string
  isLive: boolean
}

/**
 * Custom hook that conditionally fetches orders based on isLive prop
 * - If isLive is true, uses useGetAllOrdersQuery (requires startDate and endDate)
 * - If isLive is false, uses useGetOrdersQuery
 */
export const useGetTestOrLiveOrders = ({
  tenantid,
  startDate,
  endDate,
  uid,
  start,
  qty,
  role,
  isLive,
}: UseGetTestOrLiveOrdersProps) => {
  // Call both hooks but skip the one we don't need
  const {
    data: liveOrders,
    isLoading: isLiveOrdersLoading,
    isFetching: isLiveOrdersFetching,
    error: liveOrdersError,
  } = useGetAllOrdersQuery(
    {
      tenantid,
      startDate: startDate || "",
      endDate: endDate || "",
    },
    {
      skip: !isLive, // Skip if not live or missing required params
    }
  )

  const {
    data: testOrders,
    isLoading: isTestOrdersLoading,
    isFetching: isTestOrdersFetching,
    error: testOrdersError,
  } = useGetOrdersQuery(
    {
      tenantid,
      uid,
      start,
      qty,
    },
    {
      skip: isLive && role !== "admin", // Skip if live
    }
  )

  // Return the appropriate data based on live status
  return {
    data: isLive ? liveOrders : testOrders,
    isLoading: isLive ? isLiveOrdersLoading : isTestOrdersLoading,
    isFetching: isLive ? isLiveOrdersFetching : isTestOrdersFetching,
    error: isLive ? liveOrdersError : testOrdersError,
    isLive,
  }
}
